<template>
  <div class="quote-page flex flex-col min-h-screen">
    <!-- Sticky top bar: way back to the screener, plus a compact price
         summary once the hero has scrolled out of view. -->
    <div class="sticky top-0 z-40 bg-surface/85 backdrop-blur-md border-b border-line-card">
      <div class="qp-container h-14 flex items-center gap-3">
        <button type="button" @click="closeHandler" :title="`${$t('Back to the screener')} (Esc)`"
          class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 -ml-2.5 text-[13px] font-medium text-ink-3 hover:text-ink hover:bg-surface-3 transition shrink-0">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ $t("Screener") }}
        </button>
        <transition name="qp-fade">
          <div v-if="compactHeader && !loading && !loadError" class="flex items-center gap-2.5 min-w-0 border-l border-line-card pl-3">
            <img v-if="companyProfileData?.lgo" :src="companyProfileData.lgo" alt=""
              class="h-7 w-7 rounded-md object-contain ring-1 ring-line-soft bg-white shrink-0" />
            <span class="text-[14px] font-semibold text-ink shrink-0">{{ symbol }}</span>
            <span class="hidden md:inline text-[13px] text-muted truncate">{{ companyProfileData?.name }}</span>
            <span class="qp-num text-[14px] font-semibold text-ink shrink-0">{{ fmt.price(quotePriceData?.currentPrice) }}</span>
            <span class="qp-num text-[12px] font-medium shrink-0"
              :class="fmt.tone(quotePriceData?.priceChange) === 'down' ? 'text-down' : 'text-up'">
              {{ fmt.pct(quotePriceData?.priceChangePercent, 2, { signed: true }) }}
            </span>
          </div>
        </transition>
        <div class="ml-auto hidden sm:flex items-center gap-1" role="tablist" :aria-label="$t('Sections')"
          v-show="compactHeader && !loading && !loadError">
          <button v-for="tab in quoteTabs" :key="tab" type="button" role="tab"
            :aria-selected="selectedTab === tab ? 'true' : 'false'"
            class="px-3 py-1.5 rounded-lg text-[13px] font-medium transition"
            :class="selectedTab === tab ? 'bg-up-bg text-up' : 'text-muted hover:text-ink hover:bg-surface-3'"
            @click="selectTab(tab, true)">
            {{ $t(tab) }}
          </button>
        </div>
        <ThemeToggle class="shrink-0"
          :class="compactHeader && !loading && !loadError ? 'ml-auto sm:ml-3' : 'ml-auto'" />
      </div>
    </div>

    <!-- Loader: skeleton of the hero so the page has shape while loading. -->
    <div v-if="loading" class="qp-container flex-1 py-6" role="status" aria-live="polite">
      <div class="qp-card p-5 sm:p-7 animate-pulse">
        <div class="flex items-start gap-4 sm:gap-6">
          <div
            class="h-[56px] w-[56px] sm:h-[72px] sm:w-[72px] rounded-2xl bg-surface-3 flex items-center justify-center text-faint text-[24px] font-semibold shrink-0">
            {{ symbol ? symbol[0] : "" }}
          </div>
          <div class="flex flex-col gap-3 flex-1">
            <div class="h-[26px] w-[60%] max-w-[320px] rounded-md bg-surface-3"></div>
            <div class="h-[22px] w-[180px] rounded-full bg-surface-3"></div>
            <div class="h-[40px] w-[200px] rounded-md bg-surface-3 mt-2"></div>
            <div class="h-[12px] w-[160px] rounded bg-surface-3"></div>
          </div>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-6 mt-6 pt-5 border-t border-line-soft">
          <div v-for="n in 6" :key="n">
            <div class="h-[10px] w-[60%] rounded bg-surface-3"></div>
            <div class="h-[16px] w-[80%] rounded bg-surface-3 mt-2"></div>
          </div>
        </div>
      </div>
      <div class="qp-card mt-6 h-[320px] flex items-center justify-center gap-3">
        <Loading />
        <span class="text-[14px] text-muted">
          {{ $t("Loading stock details for") }} {{ symbol }}
        </span>
      </div>
    </div>

    <!-- Not found / could not load -->
    <div v-else-if="loadError" class="qp-container flex-1 py-6 flex items-start justify-center">
      <div class="qp-card w-full max-w-[520px] mt-10 p-8 text-center" role="alert">
        <div class="mx-auto h-12 w-12 rounded-2xl bg-surface-3 flex items-center justify-center text-[20px] font-semibold text-faint">
          {{ loadError === "notfound" ? "?" : "!" }}
        </div>
        <template v-if="loadError === 'notfound'">
          <h1 class="mt-4 text-[18px] font-semibold text-ink">{{ $t("Symbol not found") }}</h1>
          <p class="mt-1 text-[14px] text-muted">{{ $t("We could not load details for") }} {{ symbol }}.</p>
        </template>
        <template v-else>
          <h1 class="mt-4 text-[18px] font-semibold text-ink">{{ $t("Details unavailable") }}</h1>
          <p class="mt-1 text-[14px] text-muted">
            {{ $t(loadError === "pending" ? "load-pending-hint" : "load-error-hint", { symbol }) }}
          </p>
        </template>
        <div class="mt-5 flex items-center justify-center gap-2">
          <button v-if="loadError !== 'notfound'" type="button" @click="loadSymbol(symbol)" class="qp-btn qp-btn-primary">
            {{ $t("Retry") }}
          </button>
          <button type="button" @click="closeHandler"
            class="qp-btn" :class="loadError === 'notfound' ? 'qp-btn-primary' : 'qp-btn-ghost'">
            {{ $t("Back to the screener") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Details -->
    <div v-else class="qp-container flex-1 py-6">
      <div ref="hero">
        <QuoteHeader
          :headerData="companyProfileData"
          :price="quotePriceData"
          :symbol="symbol"
          :metricData="metricData"
          :priceMetricsData="priceMetricsData"
          :extraData="extraData"
          :oss="ossDetail"
          @openOptions="selectTab('Options', true)"
        />
      </div>

      <div ref="tabs" class="flex items-center gap-1 border-b border-line-card mt-6 mb-6 scroll-mt-20" role="tablist"
        :aria-label="$t('Sections')">
        <button
          v-for="tab in quoteTabs"
          :key="tab"
          type="button"
          role="tab"
          :aria-selected="selectedTab === tab ? 'true' : 'false'"
          class="relative px-4 py-2.5 text-[14px] font-medium -mb-px border-b-2 transition"
          :class="selectedTab === tab ? 'border-brand text-ink' : 'border-transparent text-muted hover:text-ink'"
          @click="selectTab(tab)"
        >
          {{ $t(tab) }}
          <span v-if="tab === 'Options' && ossDetail && ossDetail.oss >= 80"
            class="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-brand align-middle"></span>
        </button>
      </div>

      <QuoteOptions v-if="selectedTab === 'Options'" :symbol="symbol" :preloaded="ossDetail"
        :underlyingPrice="quotePriceData?.currentPrice" />
      <QuoteOverview
        v-if="selectedTab === 'Overview'"
        :companyProfileData="companyProfileData"
        :metricData="metricData"
        :priceMetricsData="priceMetricsData"
        :currentDividend="currentDividend"
        :performanceChartData="performanceChartData"
        :profitConversionChartData="profitConversionChartData"
        :EstimatechartsData="EstimatechartsData"
        :perrsData="perrsData"
        :candleChartData="candleChartData"
        :chartLoader="chartLoader"
        :miniChartLoader="miniChartLoader"
        :financialsError="financialsError"
        :figiNumber="figiNumber || ''"
        @getSymbol="getSymbol"
        :price="quotePriceData"
        :extraData="extraData"
        :currentEarningsData="currentEarningsData || {}"
        :earningsMove="ossDetail ? ossDetail.earningsMove : null"
        :earningsData="earningsData"
        :symbol="symbol"
      />
    </div>
    <AppFooter />
  </div>
</template>

<script>
import QuoteHeader from "../components/Quote/header.vue";
import QuoteOverview from "../components/Quote/page/Overview.vue";
import QuoteOptions from "../components/Quote/page/Options.vue";
import Loading from "../components/loading/Loading.vue";
import AppFooter from "../components/Footer.vue";
import ThemeToggle from "../components/ThemeToggle.vue";
import apiAxios from "../api/axios.js";
import { formatMixin } from "@/utils/format";

// Tab <-> `tab` query parameter (Overview is the default and has none).
const TAB_QUERY = { Overview: null, Options: "options" };

// Polling for data the server refreshes in the background: every 4 s, up to 2 min.
const REFRESH_POLL_MS = 4000;
const REFRESH_POLLS = 30;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Calendar quarter label of a period end date: "2024-12-28" -> "Q4 '24". */
export function quarterLabel(period) {
  const m = String(period || "").match(/^(\d{4})-(\d{2})/);
  if (!m) return "";
  return `Q${Math.ceil(Number(m[2]) / 3)} '${m[1].slice(-2)}`;
}

/** Local calendar date of "2026-10-28" (or an ISO timestamp), or null. */
function calendarDate(v) {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function emptyState() {
  return {
    companyProfileData: {},
    metricData: {},
    quotePriceData: {},
    currentEarningsData: {},
    priceMetricsData: {},
    dividendData: [],
    perrsData: [],
    earningsData: [],
    candleChartData: [],
    performanceChartData: {},
    profitConversionChartData: [],
    EstimatechartsData: {},
    figiNumber: null,
    // updatedAt of the stored copy on screen (null: none, or written before it existed).
    profileUpdatedAt: null,
    extraData: {},
    ossDetail: null,
    financialsError: false,
  };
}

export default {
  name: "QuotePage",
  mixins: [formatMixin],
  components: {
    QuoteHeader,
    QuoteOverview,
    QuoteOptions,
    Loading,
    AppFooter,
    ThemeToggle,
  },
  props: {
    Qoutesymbol: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      symbol: "",
      loading: true,
      // null | "notfound" | "pending" (known symbol, no data yet) | "failed"
      loadError: null,
      chartLoader: true,
      miniChartLoader: true,
      quoteTabs: ["Overview", "Options"],
      // True once the hero has scrolled under the sticky top bar.
      compactHeader: false,
      // Incremented per symbol load: responses for an older load are dropped.
      loadSeq: 0,
      ...emptyState(),
    };
  },
  computed: {
    // The tab lives in the URL (?tab=options) so reloads and shared links keep it.
    selectedTab() {
      return this.$route && this.$route.query.tab === TAB_QUERY.Options ? "Options" : "Overview";
    },
    // Most recent dividend by ex-date (the list is not guaranteed to be sorted).
    currentDividend() {
      const list = Array.isArray(this.dividendData) ? this.dividendData : [];
      if (!list.length) return null;
      return [...list].sort((a, b) => String(b?.date || "").localeCompare(String(a?.date || "")))[0];
    },
  },
  mounted() {
    this.scrollParent = this.findScrollParent();
    this.onScroll = () => this.updateCompactHeader();
    this.scrollParent.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeDestroy() {
    this.loadSeq++;
    if (this.scrollParent) this.scrollParent.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    findScrollParent() {
      let el = this.$el && this.$el.parentElement;
      while (el && el !== document.body) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return el;
        el = el.parentElement;
      }
      return window;
    },
    updateCompactHeader() {
      const hero = this.$refs.hero;
      this.compactHeader = !!hero && hero.getBoundingClientRect().bottom < 72;
    },
    scrollToTop() {
      const target = this.scrollParent || this.findScrollParent();
      if (target === window) window.scrollTo(0, 0);
      else if (target) target.scrollTop = 0;
    },

    // ---- Loading -------------------------------------------------------------------
    // Everything for one symbol. Each step checks `isCurrent()` so a slow answer
    // for a symbol the user already left never overwrites the one on screen.
    async loadSymbol(symbol) {
      const seq = ++this.loadSeq;
      const isCurrent = () => seq === this.loadSeq;
      Object.assign(this, emptyState());
      this.symbol = symbol;
      this.loading = true;
      this.loadError = null;
      this.chartLoader = true;
      this.miniChartLoader = true;
      this.compactHeader = false;
      this.scrollToTop();

      // Independent of the quote data: start right away.
      this.loadOss(symbol, isCurrent);
      this.loadCandles(symbol, isCurrent);

      // The server refreshes its stored copy from the data provider in the
      // background when it is stale, and answers this at once. The stored copy
      // is shown meanwhile and swapped for the fresh one when it lands.
      const firstRefresh = this.requestRefresh(symbol);

      let status = await this.loadProfile(symbol, isCurrent);
      if (!isCurrent()) return;
      const hadStoredCopy = status === "ok";
      if (status === "notfound") {
        // Nothing stored yet (first view of this symbol): wait for the download.
        const answer = await firstRefresh;
        if (!isCurrent()) return;
        if (answer.status !== "notfound") {
          await this.waitForRefresh(symbol, isCurrent, null, answer);
          if (!isCurrent()) return;
          status = await this.loadProfile(symbol, isCurrent);
          if (!isCurrent()) return;
          // A known symbol whose data did not arrive (refresh failed, queue full or
          // slow): "not available yet", not "not found".
          if (status === "notfound") status = "pending";
        }
      }
      this.loading = false;
      if (status !== "ok") {
        this.loadError = status;
        return;
      }
      this.loadFinancials(symbol, isCurrent);

      if (hadStoredCopy) {
        // A refresh that never lands keeps the stored copy on screen.
        const fresh = await this.waitForRefresh(symbol, isCurrent, this.profileUpdatedAt, await firstRefresh);
        if (fresh && (await this.loadProfile(symbol, isCurrent)) === "ok" && !this.miniChartLoader) {
          this.EstimatechartsData = this.estimates(this.earningsData);
        }
      }
    },

    // { status, updatedAt } of the server's stored copy, queuing a refresh when
    // it is stale. status is "notfound" or "error" when the call fails.
    async requestRefresh(symbol) {
      try {
        const res = await apiAxios.post("/stock/download-quote-data", null, { params: { symbol } });
        return res.data || {};
      } catch (e) {
        return { status: e?.response?.status === 404 ? "notfound" : "error" };
      }
    },

    // Polls until the server holds a copy newer than `since` (an updatedAt, or
    // null for none), starting from `answer`, the latest requestRefresh reply.
    // False when no newer copy is coming (failed, queue full, symbol left, timeout).
    async waitForRefresh(symbol, isCurrent, since, answer) {
      for (let i = 0; ; i++) {
        if (!isCurrent()) return false;
        // The previous API refreshed before answering and sent no status.
        if (answer.status === undefined) return true;
        if (answer.updatedAt && answer.updatedAt !== since) return true;
        if (answer.status !== "queued" || i >= REFRESH_POLLS) return false;
        await sleep(REFRESH_POLL_MS);
        if (!isCurrent()) return false;
        answer = await this.requestRefresh(symbol);
      }
    },

    async loadOss(symbol, isCurrent) {
      try {
        const res = await apiAxios.get("/stock/option-term-structure", { params: { symbol } });
        if (!isCurrent()) return;
        this.ossDetail = res.data && !res.data.isError && res.data.data ? res.data.data : null;
      } catch (e) {
        if (isCurrent()) this.ossDetail = null;
      }
    },

    async loadCandles(symbol, isCurrent) {
      try {
        const res = await apiAxios.get(`/api/v1/symbols/${encodeURIComponent(symbol)}`, {
          // compact: date + OHLCV only, what the chart reads (older APIs ignore it).
          params: { timeFrame: "D", market: "US", compact: true },
        });
        if (!isCurrent()) return;
        // Full ascending daily history: the chart slices it by the selected range.
        const candles = Array.isArray(res.data?.data) ? res.data.data : [];
        this.candleChartData = candles;
        this.extraData = {
          ...this.extraData,
          volume: candles.length ? candles[candles.length - 1]?.volume : null,
        };
      } catch (e) {
        // The chart shows its empty state.
      } finally {
        if (isCurrent()) this.chartLoader = false;
      }
    },

    // Stored profile, quote, metrics and earnings. Applies them and returns "ok",
    // or returns "notfound" / "failed" and leaves the page as it is.
    async loadProfile(symbol, isCurrent) {
      try {
        const res = await apiAxios.get("/stock/get-quote-data", { params: { symbol } });
        if (!isCurrent()) return "failed";
        const data = res?.data?.quotePage?.data;
        // Unknown symbols come back as an empty object rather than an error.
        if (!data || (!data.companyProfile?.name && !data.stockQuote?.currentPrice)) {
          return "notfound";
        }
        this.companyProfileData = data.companyProfile || {};
        this.metricData = data.stockMetric?.metric || {};
        this.quotePriceData = data.stockQuote || {};
        this.priceMetricsData = data.priceMetric?.metric || {};
        this.dividendData = Array.isArray(data.dividends) ? data.dividends : [];
        this.perrsData = Array.isArray(data.peerDetails) ? data.peerDetails : [];
        this.earningsData = Array.isArray(data.earnings?.earningCalendar) ? data.earnings.earningCalendar : [];
        this.currentEarningsData = this.getNextEarnings(this.earningsData);
        this.figiNumber = data.figi;
        this.profileUpdatedAt = res.data.quotePage.updatedAt || null;
        this.extraData = {
          ...this.extraData,
          atr: res.data?.atr,
          earningsDate: res.data?.earningsDate,
          weeklyOptionable: res.data?.weeklyOptionable,
          volatility: res.data?.volatility,
        };
        return "ok";
      } catch (error) {
        return error?.response?.status === 404 ? "notfound" : "failed";
      }
    },

    // Quarterly statements (performance + revenue-to-profit charts) and the
    // reported-vs-estimate chart. ETFs and funds have no statements.
    async loadFinancials(symbol, isCurrent) {
      try {
        let res;
        for (let i = 0; ; i++) {
          res = await apiAxios.get("/stock/get-financial-data", {
            params: { symbol, statement: "ic", freq: "quarterly" },
          });
          if (!isCurrent()) return;
          // First view of the symbol: the statements are downloaded in the background.
          if (res?.data?.data || res?.data?.status !== "queued" || i >= REFRESH_POLLS) break;
          await sleep(REFRESH_POLL_MS);
          if (!isCurrent()) return;
        }
        const list = Array.isArray(res?.data?.data?.financials) ? res.data.data.financials : [];
        // Latest seven quarters, oldest first.
        const quarters = [...list]
          .filter((q) => q && q.period)
          .sort((a, b) => String(a.period).localeCompare(String(b.period)))
          .slice(-7);
        this.performanceChartData = {
          performanceDataRevenue: quarters.map((q) => q.revenue),
          performanceDataNetIncome: quarters.map((q) => q.netIncome),
          performanceDataNetMargin: quarters.map((q) =>
            q.revenue ? Number(((q.netIncome / q.revenue) * 100).toFixed(2)) : null
          ),
          performanceDataXAxis: quarters.map((q) => quarterLabel(q.period)),
        };
        this.profitConversionChartData = this.profitConversion(quarters[quarters.length - 1]);
      } catch (e) {
        if (isCurrent()) this.financialsError = true;
      } finally {
        if (isCurrent()) {
          this.EstimatechartsData = this.estimates(this.earningsData);
          this.miniChartLoader = false;
        }
      }
    },

    // Waterfall from revenue to net income for one quarter (values in millions).
    // Each step is what is taken away (or added) between two subtotals, so the
    // bars always add up: revenue - COGS = gross profit, gross profit - operating
    // expenses = operating income, and so on down to net income.
    profitConversion(q) {
      if (!q) return [];
      const n = (v) => (v === null || v === undefined || isNaN(Number(v)) ? null : Number(v));
      const revenue = n(q.revenue);
      const cogs = n(q.costOfGoodsSold);
      const grossProfit = n(q.grossIncome) ?? (revenue !== null && cogs !== null ? revenue - cogs : null);
      const opIncome = n(q.ebit);
      const opExpenses = grossProfit !== null && opIncome !== null
        ? grossProfit - opIncome
        : (n(q.sgaExpense) || 0) + (n(q.researchDevelopment) || 0);
      const pretax = n(q.pretaxIncome);
      const netIncome = n(q.netIncome);
      const rows = [
        ["Revenue", revenue],
        ["COGS", cogs],
        ["Gross profit", grossProfit],
        ["Op. expenses", opExpenses],
        ["Op. income", opIncome],
        ["Non-op. income", pretax !== null && opIncome !== null ? pretax - opIncome : null],
        ["Taxes & other", pretax !== null && netIncome !== null ? pretax - netIncome : null],
        ["Net Income", netIncome],
      ];
      return rows.map(([x, y]) => ({ x, y: y === null ? 0 : y }));
    },

    // Reported vs estimated revenue for the latest quarters that have both.
    estimates(calendar) {
      const rows = (Array.isArray(calendar) ? calendar : [])
        .filter((e) => e && e.revenueActual && e.revenueEstimate && e.year && e.quarter)
        .sort((a, b) => a.year - b.year || a.quarter - b.quarter)
        .slice(-8);
      return {
        EpsActual: rows.map((e) => e.revenueActual),
        EpsEsitimate: rows.map((e) => e.revenueEstimate),
        EpsYear: rows.map((e) => `Q${e.quarter} '${String(e.year).slice(-2)}`),
      };
    },

    // Next earnings on or after today, compared as calendar dates so the report
    // day itself still counts in every timezone.
    getNextEarnings(calendar) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let best = null;
      let bestDate = null;
      (calendar || []).forEach((item) => {
        const d = calendarDate(item?.date);
        if (!d || d < today) return;
        if (!bestDate || d < bestDate) {
          best = item;
          bestDate = d;
        }
      });
      return best;
    },

    // ---- Navigation -------------------------------------------------------------------
    selectTab(tab, scrollToTabs = false) {
      if (tab !== this.selectedTab && this.$route) {
        const query = { ...this.$route.query };
        if (TAB_QUERY[tab]) query.tab = TAB_QUERY[tab];
        else delete query.tab;
        this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => { });
      }
      if (scrollToTabs) {
        this.$nextTick(() => {
          const tabs = this.$refs.tabs;
          if (tabs && tabs.scrollIntoView) tabs.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    },
    closeHandler() {
      this.$emit("handleClose");
    },
    // Peer click: the parent updates the URL, which feeds back through Qoutesymbol.
    getSymbol(symbol) {
      this.$emit("symbolChange", symbol);
    },
  },
  watch: {
    // The parent drives the symbol from the route (open, peers, back/forward).
    Qoutesymbol: {
      immediate: true,
      handler(newVal) {
        const next = String(newVal || "").toUpperCase();
        if (next && next !== this.symbol) this.loadSymbol(next);
      },
    },
  },
};
</script>

<style>
/* Shared tokens (.qp-*) live in src/assets/design.css. */
.quote-page {
  background: rgb(var(--qp-canvas));
}
</style>
